import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_BADGE = {
  "IA y futuro del trabajo": "badge-ia",
  "Empresa y estrategia": "badge-empresa",
  "Fundraising y narrativa": "badge-fundraising",
  "Geopolítica empresarial": "badge-geopolitica",
  "Liderazgo y construcción": "badge-liderazgo",
  "Notas breves": "badge-notas",
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
}

function NewsletterMini() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/newsletter/subscribe`, form);
      if (r.data.already_subscribed) toast.info("Ya estás suscrito.");
      else toast.success("Suscripción confirmada.");
      setForm({ name: "", email: "" });
    } catch { toast.error("Error al suscribirse."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="post-newsletter-cta" className="bg-foreground text-background p-10 mt-16">
      <p className="label-mono text-background/50 mb-3">Newsletter</p>
      <h3 className="font-serif text-2xl font-normal mb-2 text-background">¿Te ha resultado útil?</h3>
      <p className="text-sm text-background/70 mb-6 leading-relaxed">
        Suscríbete y recibe más ensayos sobre empresa, IA, estrategia y trabajo.
        Sin ruido ni relleno.
      </p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <input
          className="bg-transparent border-b border-background/30 focus:border-background/80 outline-none px-0 py-2.5 text-sm text-background placeholder:text-background/40 flex-1"
          placeholder="Nombre"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
        <input
          type="email"
          className="bg-transparent border-b border-background/30 focus:border-background/80 outline-none px-0 py-2.5 text-sm text-background placeholder:text-background/40 flex-1"
          placeholder="tu@email.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <button
          type="submit"
          disabled={loading}
          className="label-mono border border-background/30 text-background px-6 py-2.5 hover:bg-background/10 transition-colors disabled:opacity-60"
        >
          {loading ? "…" : "Suscribirme"}
        </button>
      </form>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        return axios.get(`${API}/posts?category=${encodeURIComponent(r.data.category)}&limit=4`);
      })
      .then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="pt-32 max-w-3xl mx-auto px-6 pb-24">
      <div className="h-6 bg-muted animate-pulse mb-4 w-24" />
      <div className="h-12 bg-muted animate-pulse mb-3" />
      <div className="h-12 bg-muted animate-pulse mb-8 w-3/4" />
      {[1,2,3,4].map(n => <div key={n} className="h-4 bg-muted animate-pulse mb-3" />)}
    </div>
  );

  if (!post) return (
    <div className="pt-32 text-center py-24">
      <p className="font-serif text-2xl mb-4">Artículo no encontrado.</p>
      <Link to="/ideas" className="label-mono text-primary hover:underline">← Volver a Ideas</Link>
    </div>
  );

  const paragraphs = post.content.split("\n\n").filter(Boolean);

  return (
    <div className="pt-16 md:pt-20">
      {/* Back */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-10 pb-4">
        <Link to="/ideas" data-testid="back-to-blog" className="label-mono text-muted-foreground hover:text-primary flex items-center gap-2 transition-colors">
          <ArrowLeft size={13} /> Ideas
        </Link>
      </div>

      {/* Article header */}
      <header data-testid="post-header" className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-6 pb-16 border-b border-border">
        <div className="max-w-3xl">
          <div className="flex items-center gap-4 mb-7">
            <span className={`label-mono text-xs px-2 py-1 ${CATEGORY_BADGE[post.category] || "badge-notas"}`}>
              {post.category}
            </span>
            <span className="label-mono text-muted-foreground flex items-center gap-1.5">
              <Clock size={11} /> {post.read_time} min de lectura
            </span>
            <span className="label-mono text-muted-foreground">{formatDate(post.created_at)}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal leading-[1.1] tracking-tight mb-6">
            {post.title}
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">{post.excerpt}</p>
        </div>
      </header>

      {/* Cover image */}
      {post.image_url && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-10">
          <img
            src={post.image_url}
            alt={post.title}
            data-testid="post-cover-image"
            className="w-full max-h-[480px] object-cover grayscale-[10%]"
          />
        </div>
      )}

      {/* Article body */}
      <article data-testid="post-content" className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <div className="prose-custom space-y-6">
          {paragraphs.map((para, i) => (
            <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">{para}</p>
          ))}
        </div>

        {/* Author note */}
        <div className="mt-16 pt-8 border-t border-border flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/hzqpgdt8__DSC4824.jpg"
            alt="Eduardo Delgado"
            className="w-12 h-12 object-cover object-top grayscale-[20%]"
          />
          <div>
            <p className="font-medium text-sm">Eduardo Delgado Lobejón</p>
            <p className="label-mono text-muted-foreground">CEO y cofundador de Roams</p>
          </div>
        </div>

        <NewsletterMini />
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section data-testid="related-posts" className="bg-card py-16 border-t border-border">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <p className="label-mono text-muted-foreground mb-8">Más en {post.category}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/ideas/${rp.slug}`}
                  data-testid={`related-${rp.id}`}
                  className="bg-background p-7 border border-border hover:border-primary/30 transition-colors group"
                >
                  <p className="font-serif text-lg leading-snug mb-2 group-hover:text-primary transition-colors line-clamp-3">{rp.title}</p>
                  <p className="label-mono text-muted-foreground">{rp.read_time} min</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
